import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, BACKEND_URL } from '../../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2, CheckCircle, XCircle, FileText, Download, Eye, User, Clock, AlertTriangle } from 'lucide-react';

const AmendmentsVerification = () => {
  const [pendingBooks, setPendingBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBook, setSelectedBook] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchPendingAmendments();
  }, []);

  const fetchPendingAmendments = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-amendments`);
      setPendingBooks(response.data);
    } catch (error) {
      toast.error('Kon nie data laai nie');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (approved) => {
    if (!selectedBook) return;
    
    setVerifying(true);
    try {
      const formData = new FormData();
      formData.append('approved', approved);
      if (adminNotes) formData.append('admin_notes', adminNotes);
      
      const response = await axios.post(
        `${API}/admin/verify-amendments/${selectedBook.id}`,
        formData
      );
      toast.success(response.data.message);
      setSelectedBook(null);
      setAdminNotes('');
      fetchPendingAmendments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie verifieer nie');
    } finally {
      setVerifying(false);
    }
  };

  const getFaultCategoryBadge = (category) => {
    if (category === 'Vertaling') {
      return <Badge className="bg-purple-100 text-purple-700 text-xs">Vertaling</Badge>;
    }
    return <Badge className="bg-blue-100 text-blue-700 text-xs">Oorspronklik</Badge>;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Regstellings Verifikasie
        </h2>
        <p className="text-gray-600">Verifieer regstellings wat deur skrywers en vertalers ingedien is</p>
      </div>

      {/* Pending Amendments */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Regstellings Wag vir Verifikasie ({pendingBooks.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pendingBooks.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-300" />
              <p className="font-medium">Geen regstellings wag vir verifikasie nie</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingBooks.map((book) => (
                <div 
                  key={book.id} 
                  className="p-4 border rounded-lg hover:border-orange-200 transition-colors"
                  data-testid={`amendment-book-${book.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-lg">{book.boek_naam}</h3>
                        <Badge className={book.amendments_type === 'translator' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
                          {book.amendments_type === 'translator' ? 'Vertaler' : 'Skrywer'}
                        </Badge>
                      </div>
                      <p className="text-gray-600 text-sm">deur {book.skrywer_naam}</p>
                      <div className="flex flex-wrap gap-3 mt-2 text-sm">
                        <span className="flex items-center gap-1 text-gray-500">
                          <User className="h-4 w-4" />
                          {book.submitter_name}
                        </span>
                        <span className="text-orange-600 font-medium">
                          {book.fault_count || 0} foute was gevind
                        </span>
                        {book.translation_errors > 0 && (
                          <span className="text-purple-600">
                            ({book.translation_errors} vertaalfoute)
                          </span>
                        )}
                      </div>
                      {book.amendments_notes && (
                        <p className="mt-2 text-sm text-gray-600 bg-gray-50 p-2 rounded">
                          <strong>Notas:</strong> {book.amendments_notes}
                        </p>
                      )}
                      <p className="text-gray-400 text-xs mt-1">
                        <Clock className="h-3 w-3 inline mr-1" />
                        Ingedien: {new Date(book.amendments_submitted_date).toLocaleString('af-ZA')}
                      </p>
                    </div>
                    <Button
                      onClick={() => setSelectedBook(book)}
                      className="bg-orange-500 hover:bg-orange-600"
                    >
                      <Eye className="h-4 w-4 mr-2" /> Verifieer
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Verification Dialog */}
      <Dialog open={!!selectedBook} onOpenChange={(open) => { if (!open) { setSelectedBook(null); setAdminNotes(''); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Verifieer Regstellings</DialogTitle>
          </DialogHeader>
          
          {selectedBook && (
            <div className="space-y-6">
              {/* Book Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-lg">{selectedBook.boek_naam}</h3>
                    <p className="text-gray-600">deur {selectedBook.skrywer_naam}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Ingedien deur: {selectedBook.submitter_name} ({selectedBook.amendments_type === 'translator' ? 'Vertaler' : 'Skrywer'})
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="outline"
                      data-testid="view-corrected-book-btn"
                      onClick={() => window.open(`${BACKEND_URL}/api/files/book/${selectedBook.id}?version=corrected&token=${encodeURIComponent(localStorage.getItem('token') || '')}`, '_blank')}
                    >
                      <Eye className="h-4 w-4 mr-2" /> Sien Boek (met regstellings)
                    </Button>
                    <Button
                      variant="outline"
                      data-testid="download-corrected-book-btn"
                      onClick={() => window.open(`${BACKEND_URL}/api/files/book/${selectedBook.id}?version=corrected&download=1&token=${encodeURIComponent(localStorage.getItem('token') || '')}`, '_blank')}
                    >
                      <Download className="h-4 w-4 mr-2" /> Laai Regstelde Boek Af
                    </Button>
                  </div>
                </div>
              </div>

              {/* Warning for translators */}
              {selectedBook.amendments_type === 'translator' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Vertaler Aftrekkings</p>
                      <p className="text-sm text-amber-700">
                        Die vertaler het R{(selectedBook.fault_count || 0) * 2} aftrekking ontvang vir {selectedBook.fault_count || 0} foute.
                        Vertalers is verantwoordelik vir alle foute op hulle vertaalde boeke.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Original Fault List */}
              {selectedBook.fault_list && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Oorspronklike Foute Lys ({selectedBook.fault_list.fault_entries?.length || 0} foute)
                  </h4>
                  <div className="max-h-60 overflow-y-auto space-y-2 border rounded-lg p-3 bg-white">
                    {selectedBook.fault_list.fault_entries?.map((fault, idx) => (
                      <div key={idx} className="p-2 bg-gray-50 border rounded text-sm">
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-medium">Fout #{idx + 1}</span>
                          <div className="flex gap-1">
                            {getFaultCategoryBadge(fault.fout_kategorie)}
                            <Badge variant="outline" className="text-xs">{fault.fout_tipe}</Badge>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 mb-1">
                          {fault.hoofstuk && `Hoofstuk: ${fault.hoofstuk} | `}
                          Bl. {fault.bladsy}, Par. {fault.paragraaf}, Sin {fault.sin}
                        </div>
                        <p><span className="text-red-600">Oorspronklik:</span> {fault.oorspronklike_teks}</p>
                        <p><span className="text-green-600">Regstelling:</span> {fault.voorgestelde_regstelling}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Submitter Notes */}
              {selectedBook.amendments_notes && (
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="font-medium text-blue-800 mb-1">Notas van {selectedBook.amendments_type === 'translator' ? 'Vertaler' : 'Skrywer'}:</p>
                  <p className="text-blue-700">{selectedBook.amendments_notes}</p>
                </div>
              )}

              {/* Admin Notes */}
              <div>
                <Label>Admin Notas (Opsioneel)</Label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Voeg notas by vir die indiener..."
                  rows={2}
                />
              </div>

              {/* What happens next info */}
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <p className="font-medium mb-1">Wat gebeur volgende?</p>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li><strong>Goedkeur:</strong> Boek status verander na "Gereed vir Publikasie"</li>
                  <li><strong>Afgekeur:</strong> Boek gaan terug na {selectedBook.amendments_type === 'translator' ? 'vertaler' : 'skrywer'} vir verdere regstellings</li>
                </ul>
              </div>

              <DialogFooter className="gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => { setSelectedBook(null); setAdminNotes(''); }}
                >
                  Kanselleer
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleVerify(false)}
                  disabled={verifying}
                  className="text-red-600 hover:bg-red-50"
                >
                  {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4 mr-2" />}
                  Afgekeur
                </Button>
                <Button
                  onClick={() => handleVerify(true)}
                  disabled={verifying}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                  Goedkeur
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AmendmentsVerification;
